import type { TestCaseListItem } from "@/types";

/**
 * A dropped (탈락) TC: its input is irrecoverable, so it is kept for the record
 * but never scheduled again. Not the same as retired — a person retires a TC
 * (`is_active`); a run drops one, once, with a reason. The two are independent.
 */
export function isDropped(tc: Pick<TestCaseListItem, "is_dropped">): boolean {
  // Only a literal true: an absent or malformed flag is "not dropped".
  return tc.is_dropped === true;
}

export const DROPPED_LABEL = "탈락";

const DROP_REASON_TEXT: Record<string, string> = {
  SOURCE_FILE_MISSING:
    "보관된 증거 파일을 복구할 수 없어 탈락했습니다 — 재실행 입력이 없어 Batch Test 에서 제외됩니다",
  TC_INPUT_CHRONOLOGY_UNVERIFIED:
    "질문 이전 대화의 순서를 확인할 수 없어 탈락했습니다 — 재실행 입력을 만들 수 없어 Batch Test 에서 제외됩니다",
};

export const DROPPED_REASON = "재실행 입력을 복구할 수 없어 탈락했습니다 — Batch Test 에서 제외됩니다";

/** Words for the operator; an unknown code still says what happened. */
export function droppedReasonText(code: string | null | undefined): string {
  return (code && Object.prototype.hasOwnProperty.call(DROP_REASON_TEXT, code) && DROP_REASON_TEXT[code]) || DROPPED_REASON;
}

/**
 * Dropping is for good, and BE refuses to re-activate a dropped TC
 * (`409 TC_DROPPED`). Retiring one stays open — the two are independent.
 */
export const REACTIVATE_DROPPED_TEXT = "탈락한 TC 는 다시 활성화할 수 없습니다";

/** Why "다시 활성화" is closed on a dropped, retired TC. */
export function reactivateDroppedReason(code: string | null | undefined): string {
  return `${REACTIVATE_DROPPED_TEXT} — ${droppedReasonText(code)}`;
}
